import { Alert, Box, Button, Divider, Skeleton, Typography } from '@mui/material';
import { UBWebMobile } from './UBWebMobile';
import { DataGrid } from '@mui/x-data-grid';
import { UBMetric } from "./UBMetric";
import { Link, useParams } from 'react-router-dom';
import { useFetchData } from '../../hooks/useFetchData';
import { useEffect, useMemo, useState } from 'react';
import { UBDashboardFilter } from './UBDashboardFilter';
import { UBItem } from './UBItem';
import moment from 'moment';
import { UB_DASHBOARD_FILTER_TYPE } from '../../utils/constants';

export const UBDashboard = () => {
    const { dashboard: pDashboard } = useParams();
    const [dateRange, setDateRange] = useState({
        start: moment(new Date()).startOf("day"),
        end: moment(new Date()).endOf("day"),
        filterType: UB_DASHBOARD_FILTER_TYPE.DATE_RANGE
    });
    const [dateRangeFormatted, setDateRangeFormatted] = useState({
        start: dateRange.start.format("YYYY-MM-DD HH:mm:ss"),
        end: dateRange.end.format("YYYY-MM-DD HH:mm:ss"),
    });

    const { status: categoriesStatus, data: categoriesData } = useFetchData({
        endpoint: `unionbank/${pDashboard}/categories`,
        defaultData: []
    });

    const { status: realtimeStatus, data: realtimeData } = useFetchData({
        endpoint: `unionbank/${pDashboard}/realtime`,
        defaultData: [],
        params: dateRangeFormatted
    });

    useEffect(() => {
        setDateRangeFormatted({
            start: dateRange.start.format("YYYY-MM-DD HH:mm:ss"),
            end: dateRange.end.format("YYYY-MM-DD HH:mm:ss")
        })
    }, [dateRange.end, dateRange.start, dateRange.filterType]);

    const getCategoryLabel = (categoryName) => {
        return categoriesData?.find(category => category?.category_name === categoryName)?.category_label ?? "";
    }

    const toTitleCase = (str = "") => {
        return str.toLowerCase().split('_').map(function (word) {
            return (word.charAt(0).toUpperCase() + word.slice(1));
        }).join(' ');
    }

    const getEvent = (categoryName, eventName, channelName, statusName) => {
        let event = realtimeData?.result?.find(event => event?.category_name === categoryName && event?.event_name === eventName);

        return event ? {
            label: event?.channels?.find(channel => channel?.channel_name === channelName)?.event_label ?? event?.event_label ?? "",
            tooltip: event?.channels?.find(channel => channel?.channel_name === channelName)?.event_tooltip ?? event?.event_tooltip ?? "",
            channel: toTitleCase(channelName) ?? "",
            status: toTitleCase(statusName) ?? "",
            value: event?.channels?.find(channel => channel?.channel_name === channelName)?.transaction_count ?? 0,
            responseTime: event?.response_time
        } : {label: "", tooltip: "", channel: toTitleCase(channelName) ?? "", status: toTitleCase(statusName) ?? "", value: 0, responseTime: null};
    }

    return (
        <Box
            container
            minHeight="95vh"
            height="100%"
            sx={{
                boxSizing: 'border-box',
                display: 'grid',
                gridTemplateColumns: 'repeat(8, 1fr)',
                gap: 1,
                p: 1,
            }}
        >
            <Box sx={{ gridRow: { xs: "1", sm: "1", md: "1", lg: "1" }, gridColumn: "span 8", position: "sticky" }}>
                <UBDashboardFilter subtitle='Click on the panel heading to see the Daily or Hourly view. For additional information, click on “?” (top right corner).' value={dateRange} onChange={setDateRange} />
            </Box>

            <Box sx={{ gridRow: { xs: "2", sm: "2", md: "2", lg: "2 / 4" }, gridColumn: { xs: "span 8", sm: "span 8", md: "1 / 5", lg: "1 / 3"} }}>
                <UBWebMobile id="login" sectioned titleStatus={categoriesStatus} eventsStatus={realtimeStatus} title={getCategoryLabel("login")} events={[
                    {
                        event: getEvent("login", "login"),
                        web: getEvent("login", "login", "web")?.value,
                        mobile: getEvent("login", "login", "mobile")?.value
                    },
                    {
                        event: getEvent("login", "login_assistance"),
                        web: getEvent("login", "login_assistance", "web")?.value,
                        mobile: getEvent("login", "login_assistance", "mobile")?.value
                    },
                ]} />
            </Box>

            <Box sx={{ gridRow: { xs: "3", sm: "3", md: "3 / 5", lg: "4 / 6" }, gridColumn: { xs: "span 8", sm: "span 8", md: "1 / 5", lg: "1 / 3"} }}>
                <UBMetric id="customer_account" sectioned titleStatus={categoriesStatus} eventsStatus={realtimeStatus} title={getCategoryLabel("customer_account")} events={[
                    { event_label: getEvent("customer_account", "customer_dashboard")?.label, response_time: getEvent("customer_account", "customer_dashboard")?.responseTime, metrics: [getEvent("customer_account", "customer_dashboard", "web"), getEvent("customer_account", "customer_dashboard", "mobile"), getEvent("customer_account", "customer_dashboard", "banker")] },
                    { event_label: ""},
                    { event_label: ""},
                    { event_label: getEvent("customer_account", "account_dashboard")?.label, response_time: getEvent("customer_account", "account_dashboard")?.responseTime, metrics: [getEvent("customer_account", "account_dashboard", "web"), getEvent("customer_account", "account_dashboard", "mobile"), getEvent("customer_account", "account_dashboard", "banker")] },
                ]} />
            </Box>

            <Box sx={{ gridRow: { xs: "4", sm: "4", md: "5", lg: "6 / 9" }, gridColumn: { xs: "span 8", sm: "span 8", md: "1 / 5", lg: "1 / 3"} }}>
                <UBWebMobile id="otp" sectioned titleStatus={categoriesStatus} eventsStatus={realtimeStatus} title={getCategoryLabel("otp")} events={[
                    {
                        event: { label: "Login OTP" },
                    },
                    {
                        event: getEvent("otp", "login_received"),
                        web: getEvent("otp", "login_received", "web")?.value,
                        mobile: getEvent("otp", "login_received", "mobile")?.value
                    },
                    {
                        event: getEvent("otp", "login_delivered"),
                        web: getEvent("otp", "login_delivered", "web")?.value,
                        mobile: getEvent("otp", "login_delivered", "mobile")?.value
                    },
                    {
                    },
                    {
                        event: { label: "Zelle OTP" },
                    },
                    {
                        event: getEvent("otp", "zelle_received"),
                        web: "N/A",
                        mobile: getEvent("otp", "zelle_received", "mobile")?.value
                    },
                    {
                        event: getEvent("otp", "zelle_delivered"),
                        web: "N/A",
                        mobile: getEvent("otp", "zelle_delivered", "mobile")?.value
                    },
                ]} />
            </Box>

            <Box sx={{ gridRow: { xs: "5", sm: "5", md: "2 / 3", lg: "2 / 4" }, gridColumn: { xs: "span 8", sm: "span 8", md: "5 / 9", lg: "3 / 7"} }}>
                <UBWebMobile id="enrollment" total successRate titleStatus={categoriesStatus} eventsStatus={realtimeStatus} title={getCategoryLabel("enrollment")} events={[
                    {
                        event: getEvent("enrollment", "fast_track"),
                        web: getEvent("enrollment", "fast_track", "web")?.value,
                        mobile: getEvent("enrollment", "fast_track", "mobile")?.value,
                        total: getEvent("enrollment", "fast_track", "web")?.value + getEvent("enrollment", "fast_track", "mobile")?.value,
                        successRate: (
                            (getEvent("fast_track", "account_creation", "web")?.value + getEvent("fast_track", "account_creation", "mobile")?.value) /
                            ((getEvent("fast_track", "sso", "web")?.value + getEvent("fast_track", "sso", "mobile")?.value) - (getEvent("fast_track", "already_enrolled", "web")?.value + getEvent("fast_track", "already_enrolled", "mobile")?.value)) * 100
                        )
                    },
                    {
                        event: getEvent("enrollment", "call_to_action"),
                        web: getEvent("enrollment", "call_to_action", "web")?.value,
                        mobile: getEvent("enrollment", "call_to_action", "mobile")?.value,
                        total: getEvent("enrollment", "call_to_action", "web")?.value + getEvent("enrollment", "call_to_action", "mobile")?.value,
                        successRate: (
                            (getEvent("call_to_action", "account_creation", "web")?.value + getEvent("call_to_action", "account_creation", "mobile")?.value) /
                            ((getEvent("call_to_action", "identification_attempt", "web")?.value + getEvent("call_to_action", "identification_attempt", "mobile")?.value) - (getEvent("call_to_action", "already_enrolled", "web")?.value + getEvent("call_to_action", "already_enrolled", "mobile")?.value)) * 100
                        )
                    },
                    {
                        event: {
                            label: "UB Total"
                        },
                        web:
                            getEvent("enrollment", "fast_track", "web")?.value +
                            getEvent("enrollment", "call_to_action", "web")?.value,
                        mobile:
                            getEvent("enrollment", "fast_track", "mobile")?.value +
                            getEvent("enrollment", "call_to_action", "mobile")?.value,
                        total:
                            getEvent("enrollment", "fast_track", "web")?.value + getEvent("enrollment", "fast_track", "mobile")?.value +
                            getEvent("enrollment", "call_to_action", "web")?.value + getEvent("enrollment", "call_to_action", "mobile")?.value,
                        successRate: (
                            (
                                getEvent("fast_track", "account_creation", "web")?.value + getEvent("fast_track", "account_creation", "mobile")?.value +
                                getEvent("call_to_action", "account_creation", "web")?.value + getEvent("call_to_action", "account_creation", "mobile")?.value
                            ) /
                            (
                                (getEvent("fast_track", "sso", "web")?.value + getEvent("fast_track", "sso", "mobile")?.value) - (getEvent("fast_track", "already_enrolled", "web")?.value + getEvent("fast_track", "already_enrolled", "mobile")?.value) +
                                (getEvent("call_to_action", "identification_attempt", "web")?.value + getEvent("call_to_action", "identification_attempt", "mobile")?.value) - (getEvent("call_to_action", "already_enrolled", "web")?.value + getEvent("call_to_action", "already_enrolled", "mobile")?.value)
                            )
                        ) * 100
                    },
                    {
                        event: getEvent("enrollment", "general_enrollment"),
                        web: getEvent("enrollment", "general_enrollment", "web")?.value,
                        mobile: getEvent("enrollment", "general_enrollment", "mobile")?.value,
                        total:
                            getEvent("enrollment", "general_enrollment", "web")?.value + getEvent("enrollment", "general_enrollment", "mobile")?.value,
                        successRate: (
                            (getEvent("general_enrollment", "account_creation", "web")?.value + getEvent("general_enrollment", "account_creation", "mobile")?.value) /
                            ((getEvent("general_enrollment", "identification_attempt", "web")?.value + getEvent("general_enrollment", "identification_attempt", "mobile")?.value) - (getEvent("general_enrollment", "already_enrolled", "web")?.value + getEvent("general_enrollment", "already_enrolled", "mobile")?.value)) * 100
                        )
                    },
                    {
                        event: {
                            label: "Overall Enrollment Total"
                        },
                        web:
                            getEvent("enrollment", "fast_track", "web")?.value +
                            getEvent("enrollment", "call_to_action", "web")?.value +
                            getEvent("enrollment", "general_enrollment", "web")?.value,
                        mobile:
                            getEvent("enrollment", "fast_track", "mobile")?.value +
                            getEvent("enrollment", "call_to_action", "mobile")?.value +
                            getEvent("enrollment", "general_enrollment", "mobile")?.value,
                        total:
                            getEvent("enrollment", "fast_track", "web")?.value + getEvent("enrollment", "fast_track", "mobile")?.value +
                            getEvent("enrollment", "call_to_action", "web")?.value + getEvent("enrollment", "call_to_action", "mobile")?.value +
                            getEvent("enrollment", "general_enrollment", "web")?.value + getEvent("enrollment", "general_enrollment", "mobile")?.value,
                        successRate: (
                            (
                                getEvent("fast_track", "account_creation", "web")?.value + getEvent("fast_track", "account_creation", "mobile")?.value +
                                getEvent("call_to_action", "account_creation", "web")?.value + getEvent("call_to_action", "account_creation", "mobile")?.value,
                                getEvent("general_enrollment", "account_creation", "web")?.value + getEvent("general_enrollment", "account_creation", "mobile")?.value
                            ) /
                            (
                                (getEvent("fast_track", "sso", "web")?.value + getEvent("fast_track", "sso", "mobile")?.value) - (getEvent("fast_track", "already_enrolled", "web")?.value + getEvent("fast_track", "already_enrolled", "mobile")?.value) +
                                (getEvent("call_to_action", "identification_attempt", "web")?.value + getEvent("call_to_action", "identification_attempt", "mobile")?.value) - (getEvent("call_to_action", "already_enrolled", "web")?.value + getEvent("call_to_action", "already_enrolled", "mobile")?.value) +
                                (getEvent("general_enrollment", "identification_attempt", "web")?.value + getEvent("general_enrollment", "identification_attempt", "mobile")?.value) - (getEvent("general_enrollment", "already_enrolled", "web")?.value + getEvent("general_enrollment", "already_enrolled", "mobile")?.value)
                            )
                        ) * 100
                    },
                ]} />
            </Box>

            <Box sx={{ gridRow: { xs: "6", sm: "6", md: "3", lg: "4"}, gridColumn: { xs: "span 8", sm: "span 8", md: "5 / 9", lg: "3 / 7"} }}>
                <UBItem title="Enrollment Completion Flow" titleButton={false} centeredTitle/>
            </Box>

            <Box sx={{ gridRow: { xs: "7", sm: "7", md: "4 / 6", lg: "5 / 7"}, gridColumn: { xs: "span 8", sm: "span 8", md: "5 / 9", lg: "3 / 5"} }}>
                <UBWebMobile id="fast_track" titleStatus={categoriesStatus} centeredTitle eventsStatus={realtimeStatus} title={getCategoryLabel("fast_track")} events={[
                    {
                        event: getEvent("fast_track", "token_creation"),
                        web: getEvent("fast_track", "token_creation", "web")?.value,
                        mobile: getEvent("fast_track", "token_creation", "mobile")?.value
                    },
                    {
                        event: getEvent("fast_track", "sso"),
                        web: getEvent("fast_track", "sso", "web")?.value,
                        mobile: getEvent("fast_track", "sso", "mobile")?.value
                    },
                    {
                        event: getEvent("fast_track", "already_enrolled"),
                        web: getEvent("fast_track", "already_enrolled", "web")?.value,
                        mobile: getEvent("fast_track", "already_enrolled", "mobile")?.value
                    },
                    {
                        event: getEvent("fast_track", "enrollment_start"),
                        web: getEvent("fast_track", "enrollment_start", "web")?.value,
                        mobile: getEvent("fast_track", "enrollment_start", "mobile")?.value
                    },
                    {
                        event: getEvent("fast_track", "identify_customer"),
                        web: getEvent("fast_track", "identify_customer", "web")?.value,
                        mobile: getEvent("fast_track", "identify_customer", "mobile")?.value
                    },
                    {
                        event: getEvent("fast_track", "otp_success"),
                        web: getEvent("fast_track", "otp_success", "web")?.value,
                        mobile: getEvent("fast_track", "otp_success", "mobile")?.value
                    },
                    {
                        event: getEvent("fast_track", "secondary_verification"),
                        web: getEvent("fast_track", "secondary_verification", "web")?.value,
                        mobile: getEvent("fast_track", "secondary_verification", "mobile")?.value
                    },
                    {
                        event: getEvent("fast_track", "account_creation"),
                        web: getEvent("fast_track", "account_creation", "web")?.value,
                        mobile: getEvent("fast_track", "account_creation", "mobile")?.value
                    },
                    {
                        event: getEvent("fast_track", "login_clicked"),
                        web: getEvent("fast_track", "login_clicked", "web")?.value,
                        mobile: getEvent("fast_track", "login_clicked", "mobile")?.value
                    },
                    {
                        event: getEvent("fast_track", "customer_dashboard_landing"),
                        web: getEvent("fast_track", "customer_dashboard_landing", "web")?.value,
                        mobile: getEvent("fast_track", "customer_dashboard_landing", "mobile")?.value
                    },
                ]} />
            </Box>

            <Box sx={{ gridRow: { xs: "8", sm: "8", md: "6 / 8", lg: "5 / 7"}, gridColumn: { xs: "span 8", sm: "span 8", md: "5 / 9", lg: "5 / 7"} }}>
                <UBWebMobile id="call_to_action" titleStatus={categoriesStatus} centeredTitle eventsStatus={realtimeStatus} title={getCategoryLabel("call_to_action")} events={[
                    {
                        event: getEvent("call_to_action", "already_enrolled"),
                        web: getEvent("call_to_action", "already_enrolled", "web")?.value,
                        mobile: getEvent("call_to_action", "already_enrolled", "mobile")?.value
                    },
                    {
                        event: getEvent("call_to_action", "enrollment_start"),
                        web: getEvent("call_to_action", "enrollment_start", "web")?.value,
                        mobile: getEvent("call_to_action", "enrollment_start", "mobile")?.value
                    },
                    {
                        event: getEvent("call_to_action", "identification_attempt"),
                        web: getEvent("call_to_action", "identification_attempt", "web")?.value,
                        mobile: getEvent("call_to_action", "identification_attempt", "mobile")?.value
                    },
                    {
                        event: getEvent("call_to_action", "identify_customer"),
                        web: getEvent("call_to_action", "identify_customer", "web")?.value,
                        mobile: getEvent("call_to_action", "identify_customer", "mobile")?.value
                    },
                    {
                        event: getEvent("call_to_action", "otp_success"),
                        web: getEvent("call_to_action", "otp_success", "web")?.value,
                        mobile: getEvent("call_to_action", "otp_success", "mobile")?.value
                    },
                    {
                        event: getEvent("call_to_action", "secondary_verification"),
                        web: getEvent("call_to_action", "secondary_verification", "web")?.value,
                        mobile: getEvent("call_to_action", "secondary_verification", "mobile")?.value
                    },
                    {
                        event: getEvent("call_to_action", "account_creation"),
                        web: getEvent("call_to_action", "account_creation", "web")?.value,
                        mobile: getEvent("call_to_action", "account_creation", "mobile")?.value
                    },
                ]} />
            </Box>

            <Box sx={{ gridRow: { xs: "9", sm: "9", md: "8 / 11", lg: "7 / 9"}, gridColumn: { xs: "span 8", sm: "span 8", md: "5 / 9", lg: "3 / 7"} }}>
                <UBWebMobile id="general_enrollment" centered centeredTitle titleStatus={categoriesStatus} eventsStatus={realtimeStatus} title={getCategoryLabel("general_enrollment")} events={[
                    {
                        event: getEvent("general_enrollment", "already_enrolled"),
                        web: getEvent("general_enrollment", "already_enrolled", "web")?.value,
                        mobile: getEvent("general_enrollment", "already_enrolled", "mobile")?.value
                    },
                    {
                        event: getEvent("general_enrollment", "enrollment_start"),
                        web: getEvent("general_enrollment", "enrollment_start", "web")?.value,
                        mobile: getEvent("general_enrollment", "enrollment_start", "mobile")?.value
                    },
                    {
                        event: getEvent("general_enrollment", "identification_attempt"),
                        web: getEvent("general_enrollment", "identification_attempt", "web")?.value,
                        mobile: getEvent("general_enrollment", "identification_attempt", "mobile")?.value
                    },
                    {
                        event: getEvent("general_enrollment", "identify_customer"),
                        web: getEvent("general_enrollment", "identify_customer", "web")?.value,
                        mobile: getEvent("general_enrollment", "identify_customer", "mobile")?.value
                    },
                    {
                        event: getEvent("general_enrollment", "otp_success"),
                        web: getEvent("general_enrollment", "otp_success", "web")?.value,
                        mobile: getEvent("general_enrollment", "otp_success", "mobile")?.value
                    },
                    {
                        event: getEvent("general_enrollment", "secondary_verification"),
                        web: getEvent("general_enrollment", "secondary_verification", "web")?.value,
                        mobile: getEvent("general_enrollment", "secondary_verification", "mobile")?.value
                    },
                    {
                        event: getEvent("general_enrollment", "account_creation"),
                        web: getEvent("general_enrollment", "account_creation", "web")?.value,
                        mobile: getEvent("general_enrollment", "account_creation", "mobile")?.value
                    },
                ]} />
            </Box>

            <Box sx={{ gridRow: { xs: "10", sm: "10", md: "6", lg: "2 / 4" }, gridColumn: { xs: "span 8", sm: "span 8", md: "1 / 5", lg: "7 / 9"} }}>
                <UBMetric id="bill_pay" sectioned titleStatus={categoriesStatus} eventsStatus={realtimeStatus} title={getCategoryLabel("bill_pay")} events={[
                    { event_label: getEvent("bill_pay", "bill_pay_dashboard")?.label, response_time: getEvent("bill_pay", "bill_pay_dashboard")?.responseTime, metrics: [getEvent("bill_pay", "bill_pay_dashboard", "web"), getEvent("bill_pay", "bill_pay_dashboard", "mobile")] },
                    { event_label: "" },
                    { event_label: "" },
                    { event_label: getEvent("bill_pay", "bill_pay_payment")?.label, metrics: [getEvent("bill_pay", "bill_pay_payment", "web"), getEvent("bill_pay", "bill_pay_payment", "mobile")] },
                ]} />
            </Box>
                
            <Box sx={{ gridRow: { xs: "11", sm: "11", md: "7", lg: "4 / 6" }, gridColumn: { xs: "span 8", sm: "span 8", md: "1 / 5", lg: "7 / 9"} }}>
                <UBWebMobile id="transfers" sectioned titleStatus={categoriesStatus} eventsStatus={realtimeStatus} title={getCategoryLabel("transfers")} events={[
                    {
                        event: { label: "Create Payment" },
                    },
                    {
                        event: getEvent("transfers", "internal_transfers_attempt"),
                        web: getEvent("transfers", "internal_transfers_attempt", "default")?.value,
                        mobile: getEvent("transfers", "internal_transfers_success", "default")?.value
                    },
                    {
                        event: getEvent("transfers", "external_transfers_attempt"),
                        web: getEvent("transfers", "external_transfers_attempt", "default")?.value,
                        mobile: getEvent("transfers", "external_transfers_success", "default")?.value
                    },
                    {
                        event: getEvent("transfers", "wires_transfers_attempt"),
                        web: getEvent("transfers", "wires_transfers_attempt", "default")?.value,
                        mobile: getEvent("transfers", "wires_transfers_success", "default")?.value
                    },
                    {
                        event: getEvent("transfers", "ach_transfers_attempt"),
                        web: getEvent("transfers", "ach_transfers_attempt", "default")?.value,
                        mobile: getEvent("transfers", "ach_transfers_success", "default")?.value
                    },
                ]} />
            </Box>

            <Box sx={{ gridRow: { xs: "12", sm: "12", md: "8", lg: "6" }, gridColumn: { xs: "span 8", sm: "span 8", md: "1 / 5", lg: "7 / 9"} }}>
                <UBWebMobile id="zelle" sectioned titleStatus={categoriesStatus} eventsStatus={realtimeStatus} title={getCategoryLabel("zelle")} events={[
                    {
                        event: getEvent("zelle", "token_verification"),
                        web: getEvent("zelle", "token_verification", "web")?.value,
                        mobile: getEvent("zelle", "token_verification", "mobile")?.value
                    },
                    {
                        event: getEvent("zelle", "registration"),
                        web: getEvent("zelle", "registration", "web")?.value,
                        mobile: getEvent("zelle", "registration", "mobile")?.value
                    },
                    {
                        event: getEvent("zelle", "sent"),
                        web: getEvent("zelle", "sent", "web")?.value,
                        mobile: getEvent("zelle", "sent", "mobile")?.value
                    },
                ]} />
            </Box>

            <Box sx={{ gridRow: { xs: "13", sm: "13", md: "9", lg: "7" }, gridColumn: { xs: "span 8", sm: "span 8", md: "1 / 5", lg: "7 / 9"} }}>
                <UBMetric id="mcd" compact titleStatus={categoriesStatus} eventsStatus={realtimeStatus} title={getCategoryLabel("mcd")} events={[
                    { metrics: [
                            {
                                label: getEvent("mcd", "mcd_attempt"),
                                channel: "Attempt",
                                status: "transaction",
                                value: getEvent("mcd", "mcd_attempt", "default")?.value
                            },
                            {
                                label: getEvent("mcd", "mcd_success"),
                                channel: "Successful",
                                status: "transaction",
                                value: getEvent("mcd", "mcd_success", "default")?.value
                            }
                        ]
                    },
                ]} />
            </Box>

            <Box sx={{ gridRow: { xs: "14", sm: "14", md: "10", lg: "8" }, gridColumn: { xs: "span 8", sm: "span 8", md: "1 / 5", lg: "7 / 9"} }}>
                <UBWebMobile id="wealth" centeredTitle={false} titleStatus={categoriesStatus} eventsStatus={realtimeStatus} title={getCategoryLabel("wealth")} events={[
                    {
                        event: getEvent("wealth", "login"),
                        web: getEvent("wealth", "login", "web")?.value,
                        mobile: getEvent("wealth", "login", "mobile")?.value
                    },
                    {
                        event: getEvent("wealth", "balance"),
                        web: getEvent("wealth", "balance", "web")?.value,
                        mobile: getEvent("wealth", "balance", "mobile")?.value
                    },
                    {
                        event: getEvent("wealth", "sso"),
                        web: getEvent("wealth", "sso", "web")?.value,
                        mobile: "N/A"
                    },
                ]} />
            </Box>

            <Box sx={{ gridRow: { xs: "15", sm: "15", md: "11", lg: "9"}, gridColumn: "span 8" }}>
                <UBItem id="onboarding" title={getCategoryLabel("onboarding")} titleStatus={categoriesStatus}>
                    {realtimeStatus === "loading" ? <><Skeleton /><Skeleton /><Skeleton /></> :
                        <DataGrid
                            title="test"
                            rows={[
                                {
                                    id: 1,
                                    channel: "Mobile Web",
                                    zelle: getEvent("onboarding", "zelle", "mobile_web")?.value,
                                    paperless: getEvent("onboarding", "paperless", "mobile_web")?.value,
                                    alerts: getEvent("onboarding", "alerts", "mobile_web")?.value,
                                    cc_activation: getEvent("onboarding", "cc_activation", "mobile_web")?.value,
                                    digital_wallet_checking: getEvent("onboarding", "digital_wallet_checking", "mobile_web")?.value,
                                    digital_wallet_cc: getEvent("onboarding", "digital_wallet_cc", "mobile_web")?.value,
                                    order_check: getEvent("onboarding", "order_check", "mobile_web")?.value,
                                    auto_pay: getEvent("onboarding", "auto_pay", "mobile_web")?.value,
                                    digital_deposit: getEvent("onboarding", "digital_deposit", "mobile_web")?.value,
                                    debit_card: getEvent("onboarding", "debit_card", "mobile_web")?.value,
                                    auth_user: getEvent("onboarding", "auth_user", "mobile_web")?.value,
                                },
                                {
                                    id: 2,
                                    channel: "OLB",
                                    zelle: getEvent("onboarding", "zelle", "olb")?.value,
                                    paperless: getEvent("onboarding", "paperless", "olb")?.value,
                                    alerts: getEvent("onboarding", "alerts", "olb")?.value,
                                    cc_activation: getEvent("onboarding", "cc_activation", "olb")?.value,
                                    digital_wallet_checking: getEvent("onboarding", "digital_wallet_checking", "olb")?.value,
                                    digital_wallet_cc: getEvent("onboarding", "digital_wallet_cc", "olb")?.value,
                                    order_check: getEvent("onboarding", "order_check", "olb")?.value,
                                    auto_pay: getEvent("onboarding", "auto_pay", "olb")?.value,
                                    digital_deposit: getEvent("onboarding", "digital_deposit", "olb")?.value,
                                    debit_card: getEvent("onboarding", "debit_card", "olb")?.value,
                                    auth_user: getEvent("onboarding", "auth_user", "olb")?.value,
                                },
                                {
                                    id: 0,
                                    channel: "USBAPP",
                                    zelle: getEvent("onboarding", "zelle", "usbapp")?.value,
                                    paperless: getEvent("onboarding", "paperless", "usbapp")?.value,
                                    alerts: getEvent("onboarding", "alerts", "usbapp")?.value,
                                    cc_activation: getEvent("onboarding", "cc_activation", "usbapp")?.value,
                                    digital_wallet_checking: getEvent("onboarding", "digital_wallet_checking", "usbapp")?.value,
                                    digital_wallet_cc: getEvent("onboarding", "digital_wallet_cc", "usbapp")?.value,
                                    order_check: getEvent("onboarding", "order_check", "usbapp")?.value,
                                    auto_pay: getEvent("onboarding", "auto_pay", "usbapp")?.value,
                                    digital_deposit: getEvent("onboarding", "digital_deposit", "usbapp")?.value,
                                    debit_card: getEvent("onboarding", "debit_card", "usbapp")?.value,
                                    auth_user: getEvent("onboarding", "auth_user", "usbapp")?.value,
                                }
                            ]}
                            columns={[
                                { field: 'channel', headerName: 'Channel', width: 130},
                                { field: 'zelle', headerName: getEvent("onboarding", "zelle")?.label, width: 175, type: "number"},
                                { field: 'paperless', headerName: getEvent("onboarding", "paperless")?.label, width: 175, type: "number"},
                                { field: 'alerts', headerName: getEvent("onboarding", "alerts")?.label, width: 175, type: "number"},
                                { field: 'cc_activation', headerName: getEvent("onboarding", "cc_activation")?.label, width: 175, type: "number"},
                                { field: 'digital_wallet_checking', headerName: getEvent("onboarding", "digital_wallet_checking")?.label, width: 175, type: "number"},
                                { field: 'digital_wallet_cc', headerName: getEvent("onboarding", "digital_wallet_cc")?.label, width: 175, type: "number"},
                                { field: 'order_check', headerName: getEvent("onboarding", "order_check")?.label, width: 175, type: "number"},
                                { field: 'auto_pay', headerName: getEvent("onboarding", "auto_pay")?.label, width: 175, type: "number"},
                                { field: 'digital_deposit', headerName: getEvent("onboarding", "digital_deposit")?.label, width: 175, type: "number"},
                                { field: 'debit_card', headerName: getEvent("onboarding", "debit_card")?.label, width: 175, type: "number"},
                                { field: 'auth_user', headerName: getEvent("onboarding", "auth_user")?.label, width: 175, type: "number"},
                            ]}
                            hideFooter
                            autoHeight
                        />
                    }
                </UBItem>
            </Box>
        </Box>
    );
}